import React, { useEffect, useRef } from 'react'
import Menu, { MenuProps } from 'components/menu'
import Button from 'components/button'
import Spinner from 'components/spinner'
import { useNotifications } from 'hooks/notifications'
import { IUserNotification } from 'types'
import { dateTime } from 'helpers/date'
import { validateSourceUrl } from 'helpers/utils'
import { Cross } from 'components/icons'
import { getNotificationUrl } from './url'

// Fallback titles for rows created before display snapshots existed.
const FALLBACK_TITLES: Record<string, string> = {
  comment_mention: 'You were mentioned in a comment',
  comment_reply: 'Someone replied to a comment thread',
  thread_resolved: 'Your comment thread was resolved',
  thread_reopened: 'Your comment thread was reopened',
  row_assignment: 'You were assigned in a table',
  hub_member_added: 'You were added to a hub',
  access_request: 'Someone requested access',
  sync_failed: 'A table sync failed',
  duplication_completed: 'A duplication completed',
  duplication_failed: 'A duplication failed'
}

const notificationTitle = (notification: IUserNotification): string => {
  if (notification.data?.title) return notification.data.title
  return FALLBACK_TITLES[notification.notificationType] || 'Notification'
}

const NotificationRow: React.FC<{ notification: IUserNotification }> = ({ notification }) => {
  const { markRead, dismiss } = useNotifications()

  const handleOpen = async () => {
    await markRead(notification)
    const url = getNotificationUrl(notification.data?.url, window.location.origin)
    if (url) {
      window.location.assign(url)
    }
  }

  return (
    <div
      data-testid="notification-row"
      className="flex items-start gap-2 px-2.5 py-2 hover:bg-surface-sunken cursor-pointer border-b border-divider last:border-b-0"
      role="button"
      tabIndex={0}
      aria-label={notificationTitle(notification)}
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleOpen()
        }
      }}
    >
      <img
        alt={notification.actor?.name || 'Morta'}
        className="object-cover rounded-full w-7 h-7 flex-shrink-0 mt-0.5"
        src={validateSourceUrl(notification.actor?.profilePicture)}
      />
      <div className="flex flex-col min-w-0 flex-1">
        <span
          className={`text-sm text-body whitespace-normal break-words ${notification.readAt ? '' : 'font-semibold'}`}
        >
          {notificationTitle(notification)}
        </span>
        {notification.data?.preview && <span className="text-xs text-muted truncate">{notification.data.preview}</span>}
        <span className="text-xs text-muted whitespace-normal break-words">
          {notification.project?.name ? `${notification.project.name} · ` : ''}
          {dateTime(new Date(notification.createdAt)).toLocaleString()}
        </span>
      </div>
      {!notification.readAt && (
        <span data-testid="unread-dot" className="w-2 h-2 rounded-full bg-blue mt-2 flex-shrink-0" />
      )}
      <Button
        internalType="transparent"
        aria-label="Dismiss notification"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation()
          dismiss(notification)
        }}
      >
        <Cross />
      </Button>
    </div>
  )
}

const NotificationsPanel: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { notifications, unreadCount, loading, hasMore, loadNotifications, markAllRead } = useNotifications()

  // The unread count as this panel last saw it.
  const previousUnreadCount = useRef<number>(unreadCount)

  useEffect(() => {
    if (menuState.open) {
      loadNotifications(true)
    }
  }, [menuState.open])

  // The badge poll keeps unreadCount live while the panel sits open, but it asks
  // the server for no rows. Only the server ever raises that count -- every
  // change made from here lowers it, as things are read or dismissed -- so a
  // rise means a notification arrived after the list was fetched, and the panel
  // is offering "Mark all read" over "You're all caught up" (#10705).
  //
  // This is the only thing that fetches beyond opening the panel, and it asks
  // for rows only when there is one to collect: nothing happens while the panel
  // is shut, nor while the count is level or falling.
  //
  // A load's own response carries a count too, and reads the same as a poll's.
  // So when the badge was behind at the moment the panel opened, that response
  // costs one further refresh, which returns the rows it already had. Telling
  // the two apart means tracking the count each response arrived with, which is
  // more machinery than one spare request is worth. It cannot repeat: the ref
  // has moved on by then, so the pass after it sees no rise.
  useEffect(() => {
    if (!menuState.open) {
      // Keep pace while shut, so opening is never mistaken for an arrival.
      previousUnreadCount.current = unreadCount
      return
    }
    // A load is already on its way and will bring the new row with it.
    if (loading) return
    const rose = unreadCount > previousUnreadCount.current
    previousUnreadCount.current = unreadCount
    if (rose) {
      loadNotifications(true)
    }
  }, [menuState.open, unreadCount, loading])

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width} viewportMargin={10} fixed>
      <div className="text-base pt-[5px] pb-2.5">
        <div className="sticky top-0 z-10 bg-surface w-full flex items-center justify-between text-body font-bold p-2.5">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button internalType="secondary" onClick={() => markAllRead()}>
              Mark all read
            </Button>
          )}
        </div>

        {notifications.length === 0 && !loading && (
          <div className="w-full flex items-center justify-center text-muted text-sm p-5">
            You&apos;re all caught up
          </div>
        )}

        {notifications.map((notification) => (
          <NotificationRow key={notification.publicId} notification={notification} />
        ))}

        {loading && (
          <div className="w-full flex items-center justify-center p-2.5">
            <Spinner />
          </div>
        )}

        {hasMore && !loading && (
          <div className="w-full flex items-center justify-center p-2.5">
            <Button internalType="grey" onClick={() => loadNotifications()}>
              Load more
            </Button>
          </div>
        )}
      </div>
    </Menu>
  )
}

export default NotificationsPanel
